import React from 'react';
import { Modal } from '@spotify-internal/anchor-ds';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import { useT } from 'client/i18n/hooks/useT';
import Box from '../../../shared/Box';
import Heading from '../../../shared/Heading';
import Icon from '../../../shared/Icon';
import { If } from '../../../shared/If';
import Image from '../../../shared/Image';
import { LinkText } from '../../../shared/Link';
import { Mask } from '../../../shared/Mask';
import Text from '../../../shared/Text';
import {
  // @ts-ignore
  appStoreLink,
  // @ts-ignore
  isAndroidChrome,
  // @ts-ignore
  isIOS,
  // @ts-ignore
  playStoreLink,
} from '../../../../helpers/serverRenderingUtils';

interface CallEndedOrFullProps {
  isShowingLeaveConfirmationModal: boolean;
  isUserLoggedIn: boolean;
  onPressDoneRecordingConfirmation: any;
  scene: 'ended' | 'full';
  podcastName: string;
  podcastCreatorUserName: string;
  coverartImageUrl: string;
}

const CallEndedOrFull = ({
  isShowingLeaveConfirmationModal,
  isUserLoggedIn,
  onPressDoneRecordingConfirmation,
  scene,
  podcastName,
  podcastCreatorUserName,
  coverartImageUrl,
}: CallEndedOrFullProps) => {
  const t = useT();
  return (
    <Box
      width="100%"
      smPaddingLeft={33}
      smPaddingRight={33}
      mdPaddingRight={33}
      mdPaddingLeft={33}
    >
      <Box
        display="flex"
        justifyContent="center"
        width="100%"
        smPaddingBottom={20}
        mdPaddingBottom={20}
        lgPaddingBottom={32}
        smPaddingTop={24}
        mdPaddingTop={24}
        lgPaddingTop={53}
      >
        <Box maxWidth={600} width="100%">
          <Heading
            sizeAtSmScreen="sm"
            sizeAtMdScreen="md"
            sizeAtLgScreen="md"
            color="#292F36"
            isBold={true}
            align="center"
          >
            {podcastName}
          </Heading>
        </Box>
      </Box>
      <Box display="flex" justifyContent="center" width="100%">
        <Box maxWidth={650} width="100%">
          <Text color="#7F8287" size="xl" align="center">
            {scene === 'full'
              ? t(
                  'CallJoin.CallEndedOrFull.CallFullheader',
                  'This recording with {podcastCreatorUserName} is already full',
                  'Call with friends feature, call already full message',
                  {
                    podcastCreatorUserName: (
                      <Text
                        color="#7F8287"
                        size="xl"
                        align="center"
                        isInline={true}
                        isBold={true}
                        key={`calljoin-full--user-${
                          podcastCreatorUserName || 'anonymous'
                        }`}
                      >
                        {`${
                          podcastCreatorUserName ||
                          t(
                            'CallJoin.CallEndedOrFull.AnonymousCaller',
                            'Anonymous',
                            'Call with friends feature, Anonymous caller'
                          )
                        }`}
                      </Text>
                    ),
                  }
                )
              : scene === 'ended'
              ? t(
                  'CallJoin.CallEndedOrFull.CallEndedHeader',
                  'This recording with {podcastCreatorUserName} has already ended',
                  'Call with friends feature, call already ended message',
                  {
                    podcastCreatorUserName: (
                      <Text
                        color="#7F8287"
                        size="xl"
                        align="center"
                        isInline={true}
                        isBold={true}
                        key={`calljoin-ended--user-${
                          podcastCreatorUserName || 'anonymous'
                        }`}
                      >
                        {`${
                          podcastCreatorUserName ||
                          t(
                            'CallJoin.CallEndedOrFull.AnonymousCaller',
                            'Anonymous',
                            'Call with friends feature, Anonymous caller'
                          )
                        }`}
                      </Text>
                    ),
                  }
                )
              : ''}
          </Text>
        </Box>
      </Box>
      <If
        condition={isIOS() || isAndroidChrome()}
        ifRender={() => (
          <Box
            display="flex"
            justifyContent="center"
            width="100%"
            marginTop={20}
          >
            <Box maxWidth={650} width="100%">
              <Text color="#7F8287" size="xl" align="center">
                {t(
                  'CallJoin.CallEndedOrFull.InviteFriendsAnchorAppMessage',
                  'Try inviting friends to your own recording from the {anchorAppLink}!',
                  'Call with friends feature, invite them to use Anchor app message',
                  {
                    anchorAppLink: (
                      <LinkText
                        key="anchor-app-link"
                        to={isIOS() ? appStoreLink() : playStoreLink()}
                        color="purple"
                        target="_blank"
                        isInline={true}
                      >
                        {t(
                          'CallJoin.CallEndedOrFull.AnchorMobileAppLink',
                          'Anchor mobile app',
                          'Anchor mobile app link text'
                        )}
                      </LinkText>
                    ),
                  }
                )}
              </Text>
            </Box>
          </Box>
        )}
      />
      <Box>
        <Box display="flex" justifyContent="center" marginTop={45}>
          <Box width={140} height={140}>
            <Mask
              isFullWidth={true}
              isFullHeight={true}
              shape="rounded"
              boxShadow="0 2px 6px 0 rgba(0,0,0,0.4)"
            >
              <If
                condition={Boolean(coverartImageUrl)}
                ifRender={() => (
                  <Image
                    width={140}
                    height={140}
                    imageUrl={coverartImageUrl}
                    retinaImageUrl={coverartImageUrl}
                  />
                )}
                elseRender={() => (
                  <Box
                    width="100%"
                    height="100%"
                    color="#DEDFE0"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box width={55}>
                      <Icon type="anchor_logo" fillColor="white" />
                    </Box>
                  </Box>
                )}
              />
            </Mask>
          </Box>
        </Box>
      </Box>
      <Modal
        size="small"
        aria-labelledby="your-done-recording-title"
        isShowing={isShowingLeaveConfirmationModal}
      >
        <Modal.Title id="your-done-recording-title">
          {t(
            'CallJoin.CallEndedOrFull.RecordingFinishedModalTitle',
            "You're done recording",
            'Call with friends feature, recording finished modal title'
          )}
        </Modal.Title>
        <Modal.Body>
          {' '}
          {t(
            'CallJoin.CallEndedOrFull.RecordingFinishedModalMessage',
            'Nice job! That sounded great.',
            'Call with friends feature, recording finished modal message'
          )}
        </Modal.Body>
        <Modal.Footer>
          <ButtonPrimary
            onClick={() => {
              onPressDoneRecordingConfirmation(isUserLoggedIn);
            }}
          >
            {t(
              'Button.GotItThanks',
              'Got it, thanks',
              'Confirmation button text'
            )}
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export { CallEndedOrFull };
